import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, EyeOff, Type, Repeat, Sparkles, Mic } from "lucide-react";

const features = [
  { icon: EyeOff, title: "Fill in the blanks", body: "Words fade out one by one. Your voice fills them back in." },
  { icon: Type, title: "First-letter drill", body: "Only the first letter of each word. A gentle nudge, not a crutch." },
  { icon: Mic, title: "Sing it out loud", body: "Record yourself into the mic — hear it back, see word-for-word how close you were." },
  { icon: Repeat, title: "Spaced practice", body: "Come back on the right day. Not too soon, not too late." },
  { icon: Sparkles, title: "AI Coach", body: "When you're stuck, one calm hint — never the answer." },
];

export default function Landing() {
  return (
    <main data-testid="landing-page" className="text-foreground">
      {/* Hero — full bleed vintage mic photograph with warm scrim */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10">
          <img
            data-testid="landing-hero-image"
            src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=2000&auto=format&fit=crop&q=70"
            alt="Vintage condenser microphone in warm spotlight"
            className="w-full h-full object-cover opacity-60"
          />
          {/* Warm amber vignette + bottom fade to background */}
          <div className="absolute inset-0"
               style={{ background: "radial-gradient(ellipse 900px 500px at 20% 40%, rgba(229,138,63,0.28), transparent 60%)" }} />
          <div className="absolute inset-0"
               style={{ background: "linear-gradient(180deg, rgba(15,16,20,0.35) 0%, rgba(15,16,20,0.85) 65%, hsl(var(--background)) 100%)" }} />
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-28 md:pb-40 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 animate-fade-up">
            <p className="overline-label text-accent mb-6" data-testid="landing-eyebrow">
              A practice room for singers
            </p>
            <h1 className="font-display font-medium text-[13vw] md:text-[7.5rem] lg:text-[9rem] leading-[0.92] tracking-tight">
              Never lose <em className="text-primary font-normal">your&nbsp;place</em>
              <span className="block">on stage again.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Lyric Lock is a quiet, repetitive practice room. Add a song, work through gentle drills,
              let spaced repetition set the lyrics deep — so nerves can't shake them loose.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/auth?mode=register">
                <Button data-testid="landing-cta-primary" size="lg"
                        className="rounded-full h-12 px-7 text-base glow-amber-sm hover:glow-amber transition-shadow duration-300">
                  Start memorizing <ArrowRight size={18} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/auth?mode=login">
                <Button data-testid="landing-cta-secondary" size="lg" variant="ghost"
                        className="rounded-full h-12 px-6 text-base text-foreground hover:text-primary hover:bg-transparent">
                  I already have an account
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">Simple. Free to start. No card, no fluff.</p>
          </div>

          {/* Lyric drill preview card */}
          <div className="md:col-span-4 md:mb-2 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="grain-panel border border-border bg-card/85 backdrop-blur-md rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <p className="overline-label text-accent">Verse 1 · Blanks</p>
                <span className="font-mono text-xs text-muted-foreground">02:14</span>
              </div>
              <p className="lyric-line mt-4 text-2xl text-foreground">
                I remember <span className="inline-block bg-secondary border-b-2 border-primary rounded-sm px-3 mx-0.5">_____</span> in the winter,
                you were <span className="inline-block bg-secondary border-b-2 border-primary rounded-sm px-3 mx-0.5">_____</span> like a song.
              </p>
              <div className="mt-6 flex items-center justify-between">
                <span className="font-mono text-xs uppercase text-muted-foreground tracking-[0.2em]">Combo</span>
                <span className="font-mono text-primary text-lg glow-amber-sm px-3 py-0.5 rounded-full border border-primary/40">3×</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features — asymmetric bento */}
      <section>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <p className="overline-label text-accent mb-4">How it works</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl leading-tight">
            Five calm ways to get a song<br className="hidden md:block" />
            <em className="text-primary font-normal">into your bones.</em>
          </h2>

          <div className="mt-14 grid md:grid-cols-6 gap-4">
            {features.map((f, i) => (
              <article
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`grain-panel group relative border border-border bg-card rounded-2xl p-7 transition-colors duration-300 hover:border-primary/60
                  ${i === 0 ? "md:col-span-3 md:row-span-2" : i === 2 ? "md:col-span-3" : "md:col-span-2"}`}
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-primary/40 text-primary transition-shadow duration-300 group-hover:glow-amber-sm">
                  <f.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-2xl md:text-3xl tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Studio strip */}
      <section className="relative overflow-hidden border-y border-border">
        <img
          src="https://images.unsplash.com/photo-1520166012956-add9ba0835cb?w=2000&auto=format&fit=crop&q=70"
          alt="Analog mixing desk"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(90deg, hsl(var(--background)) 5%, transparent 40%, transparent 60%, hsl(var(--background)) 95%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-28 text-center">
          <p className="overline-label text-accent">One song at a time</p>
          <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-tight max-w-3xl mx-auto">
            Practice quietly.
            <span className="block italic text-primary">Perform confidently.</span>
          </h2>
          <Link to="/auth?mode=register">
            <Button data-testid="landing-cta-bottom" size="lg"
                    className="mt-10 rounded-full h-12 px-8 glow-amber-sm hover:glow-amber transition-shadow duration-300">
              Lock in your first song
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
